

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UserCog } from 'lucide-react';
import {
  Card,
  Grid,
  Box,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';

import { useAuth } from "../../auth/Auth";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDUserAvatar from "components/MDUserAvatar";
import MDButton from "components/MDButton";
import { useCapability } from "auth/usePermission";
import moment from 'moment';
import useUiVersion from "hooks/useUiVersion";
import UserProfileV2 from "./UserProfileV2";

function ViewUserProfile() {
  const { userId } = useParams();
  const { axiosInstance } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  // v2: people who manage users can jump to the user's access record.
  const { isV2 } = useUiVersion();
  const canManageUsers = useCapability('MANAGE_USERS');
  const canManageAccess = isV2 && canManageUsers;

  const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'Not available';
    return moment(dateTime).format('MMM DD, YYYY, hh:mm A');
  };

  const formatFullName = (firstName, middleName, lastName) => {
    const names = [
      capitalizeFirstLetter(firstName),
      capitalizeFirstLetter(middleName),
      capitalizeFirstLetter(lastName)
    ].filter(Boolean); // This removes empty strings

    return names.join(' ') || 'No Name Set';
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosInstance.get(`/users/${userId}/profile`);
        setProfileData(response.data);
      } catch (error) {
        console.error('Error fetching user profile:', error);
        // Only the v2 page reads this; the classic page is left as it was.
        setError(error?.response?.status === 404 ? 'This user does not exist.' : 'This profile could not be loaded. Try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [userId]);

  const renderSection = (title, fields) => (
    <MDBox
      mb={2}
      p={2}
      borderRadius="lg"
      sx={{
        backgroundColor: '#ffffff',
        boxShadow: 3
      }}
    >
      <MDTypography variant="h6" fontWeight="medium" color="dark" mb={2}>
        {title}
      </MDTypography>
      <Grid container spacing={2}>
        {fields.map((item) => (
          <Grid item xs={12} md={6} key={item.field}>
            <MDBox display="flex" alignItems="center" gap={1}>
              {item.icon && (
                <Box sx={{ color: 'info.main' }}>
                  {item.icon}
                </Box>
              )}
              <MDBox>
                <MDTypography variant="button" fontWeight="regular" color="text">
                  <strong>{item.label || item.field}:</strong>
                </MDTypography>
                <MDTypography
                  component="span"
                  variant="button"
                  fontWeight="regular"
                  sx={{ ml: 1 }}
                >
                  {item.value || profileData[item.field] || 'Not available'}
                </MDTypography>
              </MDBox>
            </MDBox>
          </Grid>
        ))}
      </Grid>
    </MDBox>
  );

  if (isV2) {
    return (
      <UserProfileV2
        profile={profileData}
        loading={loading}
        error={error}
        canManageAccess={canManageAccess}
        onManageAccess={() => navigate(`/settings/users/${userId}`)}
      />
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="calc(100vh - 200px)"
        >
          <CircularProgress color="info" />
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox px={2} py={2}>
        {canManageAccess && (
          <MDBox display="flex" justifyContent="flex-end" mb={2}>
            <MDButton variant="outlined" color="dark" startIcon={<UserCog size={16} />} onClick={() => navigate(`/settings/users/${userId}`)}>
              Manage access
            </MDButton>
          </MDBox>
        )}
        <Grid container spacing={2}>
          {/* Header Card */}
          <Grid item xs={12}>
            <Card
              sx={{
                position: 'relative',
                overflow: 'visible',
                mb: 3,
                background: 'linear-gradient(195deg, #1565C8, #5B9BD5)',
                boxShadow: '0 8px 20px -12px rgba(0, 158, 219, 0.6)'
              }}
            >
              <MDBox
                position="relative"
                p={3}
                display="flex"
                flexDirection={{ xs: 'column', md: 'row' }}
                alignItems="center"
                gap={2}
              >
                {/* Avatar Section */}
                <MDUserAvatar
                  firstName={profileData?.first_name}
                  middleName={profileData?.middle_name}
                  lastName={profileData?.last_name}
                  avatarUrl={profileData?.avatar}
                  size={150}
                  variant="rounded"
                />

                {/* Profile Info */}
                <MDBox flex={1} color="white">
                  <MDTypography variant="h4" fontWeight="medium" color="white">
                    {formatFullName(
                      profileData?.first_name,
                      profileData?.middle_name,
                      profileData?.last_name
                    )}
                  </MDTypography>
                  <MDTypography variant="body2" color="white" opacity={0.8}>
                    {profileData?.designation || 'No designation set'}
                  </MDTypography>
                  <MDTypography variant="caption" color="white" opacity={0.8}>
                    {profileData?.department} • {profileData?.email}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          {/* Profile Information */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={2}>
                {renderSection('Profile Information', [
                  {
                    field: 'full_name',
                    label: 'Full Name',
                    value: formatFullName(
                      profileData?.first_name,
                      profileData?.middle_name,
                      profileData?.last_name
                    ),
                  },
                  { field: 'username', label: 'Username' },
                  { field: 'email', label: 'Email' },
                  { field: 'phone', label: 'Phone' },
                  { field: 'department', label: 'Department' },
                  { field: 'designation', label: 'Designation' },
                  { 
                    field: 'role',
                    label: 'Role',
                    value: profileData?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
                  },
                  { field: 'joining_date', label: 'Joining Date', value: formatDateTime(profileData?.joining_date) },
                  { field: 'last_login', label: 'Last Login', value: formatDateTime(profileData?.last_login) }
                ])}

                {renderSection('Reporting Information', [
                  { field: 'manager_name', label: 'Reports To' },
                ])}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      
    </DashboardLayout>
  );
}

export default ViewUserProfile;